export class ApiError extends Error {
  constructor(public status: number, public payload: any, message: string) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: any): Promise<T> {
  const res = await fetch(`/api/v1${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as unknown as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, data, data?.error ?? `HTTP ${res.status}`);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: any) => request<T>('POST', path, body),
  put: <T>(path: string, body?: any) => request<T>('PUT', path, body),
  delete: <T>(path: string, body?: any) => request<T>('DELETE', path, body),
  async uploadImage(entity: 'technicians' | 'equipment' | 'accessories' | 'consumables' | 'customers' | 'orders' | 'parts' | 'company', file: File): Promise<{ path: string }> {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`/api/v1/uploads/${entity}`, { method: 'POST', credentials: 'include', body: fd });
    const data = await res.json();
    if (!res.ok) throw new ApiError(res.status, data, data?.error ?? `HTTP ${res.status}`);
    return data;
  },
};

/**
 * Fetch a file endpoint (with the session cookie) and trigger a browser
 * download. Uses the server's Content-Disposition filename when present,
 * otherwise `fallbackName`. Throws ApiError on a non-OK response.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`/api/v1${path}`, { method: 'GET', credentials: 'include' });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      msg = data?.error ?? msg;
    } catch { /* non-JSON error body */ }
    throw new ApiError(res.status, null, msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') ?? '';
  // Prefer RFC 5987 filename*=UTF-8''…, then plain filename="…".
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  const name = star ? decodeURIComponent(star[1]) : (plain ? plain[1] : fallbackName);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function imageUrl(relPath: string | null | undefined, thumb = false): string | undefined {
  if (!relPath) return undefined;
  if (!thumb) return `/images/${relPath}`;
  const parts = relPath.split('/');
  const file = parts.pop()!;
  return `/images/${[...parts, 'thumbs', file].join('/')}`;
}
